import { getLocalApi } from "./localApi";
import apiConfig from "app/api/apiConfig.json";
import { Port, Terminal, TransferInstruction } from "app/shared/model/Port";
import { ApiErrors } from "./ApiErrors";

const PORT_TYPE_ID_MAP: Record<string, number> = {
  "Airport": 1,
  "Train Station": 2,
};

function toPositiveInteger(value: unknown, fallback?: number): number | undefined {
  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed > 0) {
    return Math.floor(parsed);
  }
  if (fallback !== undefined) {
    const fallbackParsed = Number(fallback);
    if (Number.isFinite(fallbackParsed) && fallbackParsed > 0) {
      return Math.floor(fallbackParsed);
    }
  }
  return undefined;
}

function toDto(port: Port) {
  return {
    id: port.id ?? 0,
    name: port.name,
    isActive: !!port.active,
    isLocked: false,
    transferPortTypeId: PORT_TYPE_ID_MAP[port.type ?? ""] ?? 0,

    terminals: (port.terminals ?? []).map((t: Terminal) => {
      const numberParsed = Number(t.number);
      const number = (Number.isFinite(numberParsed) && numberParsed === 0) 
        ? 0 
        : toPositiveInteger(t.number);
      const order = toPositiveInteger(t.order, number);
      const dto: Record<string, unknown> = {
        name: t.name,
        isActive: !!t.active,
      };
      if (number !== undefined) {
        dto.number = number;
      }
      if (order !== undefined) {
        dto.order = order;
      }
      return dto;
    }),

    transferInstructions: (port.transferInstructions ?? []).map(
      (ti: TransferInstruction) => ({
        divisionId: ti.divisionId ?? 0,
        instructions: ti.instructions?.trim() || "",
      })
    ),
  };
}

export async function savePortWithTerminals(port: Port): Promise<{ ok: boolean; data?: any; message?: string; errors?: string[] }> {
  const localApi = await getLocalApi();
  const payload = toDto(port);
  const res = await localApi.call(
    (apiConfig as any).post_ports_with_terminals,
    "POST",
    JSON.stringify(payload)
  );

  if (!res) return { ok: false, message: "No response from API", errors: ["No response from API"] };
  if (!res.ok || (res.status !== 200 && res.status !== 201)) {
    const text = await res.text().catch(() => "");
    let parsed: any = null;
    if (text) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = text;
      }
    }
    const errors = ApiErrors.extractAllErrors(parsed, res, "Failed to save port");
    return { ok: false, message: ApiErrors.formatErrors(errors), errors };
  }

  const data = await res.json().catch(() => undefined);
  return { ok: true, data };
}